import React, { Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';
import { BlackBorderButton } from 'components/Button';
import CurveThumbnail from 'components/CurveThumbnail';
import {
  CurveThumbnailContainer,
  LabledInput,
  InputUnitLabel,
  InputAndLabelContainer,
  Dropdown
} from 'components/PanelComponents';
import Text from 'components/Text';
import {
  CONTROL_TYPES,
  defaultItemsToDisplayInSingleRow,
  defaultRowHeight,
  defaultSecondaryRowHeight,
  OP_MOD_FIXED_VAR_REF_TYPES,
  RESOURCE_TYPES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import { useDispatch } from 'react-redux';
import { setSidePanel } from 'reducers/sidePanel/actions';
import { ReactComponent as RemoveIcon } from '../assets/remove-icon.svg';
import Table from './Table';
import { displayFloatingPoint, formatCurveDataValues } from 'utils/format';

const curveColumnDefs = [
  {
    headerName: 'Curve',
    field: 'curveDataArray',
    width: 60,
    cellRenderer: ({ value }) => (
      <CurveThumbnailContainer>
        <CurveThumbnail curveData={value} />
      </CurveThumbnailContainer>
    )
  },
  {
    headerName: 'Curve ID',
    field: 'curveId',
    width: 60
  },
  {
    headerName: 'Curve Points',
    field: 'curveDataArray',
    cellRenderer: row => {
      // Limit the amount of items to display
      const { curveDataArray = [], xMultiplier, yMultiplier } = row.data;
      const curveDataDisplayPoints =
        curveDataArray.length > defaultItemsToDisplayInSingleRow
          ? curveDataArray
              .slice(0, defaultItemsToDisplayInSingleRow)
              .concat(['...'])
          : curveDataArray;
      return (
        <CoordinatesContainer>
          {curveDataDisplayPoints.map((coords, i) => {
            const hasXValue = Object.prototype.hasOwnProperty.call(
              coords,
              'xvalue'
            );
            const hasYValue = Object.prototype.hasOwnProperty.call(
              coords,
              'yvalue'
            );
            if (hasXValue && hasYValue) {
              return (
                <CoordinateCoordinate key={i}>
                  ({formatCurveDataValues(coords.xvalue, xMultiplier)},{' '}
                  {formatCurveDataValues(coords.yvalue, yMultiplier)})
                </CoordinateCoordinate>
              );
            }
            return (
              <CoordinateCoordinate key={i}>{coords}</CoordinateCoordinate>
            );
          })}
        </CoordinatesContainer>
      );
    }
  }
];

const ControlTypeFormWrapper = styled.div`
  margin-left: 16px;
`;

const ControlTypeBooleans = styled.div``;

const RadioLabel = styled.label`
  display: block;
  font-size: ${({ fontSize }) => fontSize};
`;

const RadioButton = styled.input`
  margin-right: 10px;
`;

const PanelRow = styled.div`
  height: 26px;
  display: flex;
  align-items: center;
  margin: 8px 0;
`;

const RowDescriptor = styled.p`
  width: 175px;
  font-size: 12px;
`;

const CurveDisplayRow = styled.div`
  display: flex;
`;

const RemoveCurveButton = styled.button`
  margin-right: 8px;
  background: white;
  :hover {
    cursor: pointer;
  }
`;

const RadioBooleans = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CoordinatesContainer = styled.ul`
  margin: 0 auto;
`;

const CoordinateCoordinate = styled.li`
  padding: 2px;
`;

const controlModeNames = {
  opModTargetW: {
    radio: 'opModTargetWRadio',
    value: 'opModTargetWInputValue'
  },
  opModFixedW: {
    radio: 'opModFixedWRadio',
    value: 'opModFixedWInputValue'
  }
};

const ControlTypeForm = ({
  handleFormChange,
  index,
  setWaitingForReturn,
  controlObj,
  removeCurve,
  isError,
  controlType
}) => {
  const [opModFixedWInputValue, setOpModFixedWInputValue] = useState('');
  const [opModFixedPFAbsorbWInputValue, setOpModFixedPFAbsorbWInputValue] =
    useState('');
  const [opModFixedPFInjectWInputValue, setOpModFixedPFInjectWInputValue] =
    useState('');
  const [opModTargetWInputValue, setOpModTargetWInputValue] = useState('');
  const [opModFixedWRadioValue, setOpModFixedWRadioValue] = useState(0);
  const [opModTargetWRadioValue, setOpModTargetWRadioValue] = useState(0);

  // opModFixedPFAbsorbW and opModFixedPFInjectW
  useEffect(() => {
    if (
      controlObj.controlType === 'opModFixedPFAbsorbW' ||
      controlObj.controlType === 'opModFixedPFInjectW'
    ) {
      const displayValue = parseFloat(
        displayFloatingPoint(controlObj.displacement, controlObj.multiplier)
      );
      if (controlObj.controlType === 'opModFixedPFAbsorbW') {
        setOpModFixedPFAbsorbWInputValue(displayValue);
      } else if (controlObj.controlType === 'opModFixedPFInjectW') {
        setOpModFixedPFInjectWInputValue(displayValue);
      }
    }
  }, []);

  // opModFixedW and opModTargetW
  useEffect(() => {
    if (controlObj.controlType === 'opModFixedW') {
      const value = controlObj.opModFixedW;
      const isPositive = value >= 0;
      setOpModFixedWInputValue(Math.abs(value));
      setOpModFixedWRadioValue(isPositive ? 0 : 1);
    } else if (controlObj.controlType === 'opModTargetW') {
      const value = controlObj.value;
      const isPositive = value >= 0;
      setOpModTargetWInputValue(Math.abs(value));
      setOpModTargetWRadioValue(isPositive ? 0 : 1);
    }
  }, []);

  const dispatch = useDispatch();
  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    handleFormChange({
      index,
      key: name,
      value: value
    });
  };

  const handleChargeDischargeChange = (event, controlName) => {
    const modeOptions = {
      opModFixedW: {
        inputValue: opModFixedWInputValue,
        inputName: controlModeNames.opModFixedW.value,
        radioValue: opModFixedWRadioValue,
        radioName: controlModeNames.opModFixedW.radio,
        setInputValue: setOpModFixedWInputValue,
        setRadioValue: setOpModFixedWRadioValue,
        keyName: 'opModFixedW'
      },
      opModTargetW: {
        inputValue: opModTargetWInputValue,
        inputName: controlModeNames.opModTargetW.value,
        radioValue: opModTargetWRadioValue,
        radioName: controlModeNames.opModTargetW.radio,
        setInputValue: setOpModTargetWInputValue,
        setRadioValue: setOpModTargetWRadioValue,
        keyName: 'value'
      }
    };
    const target = event.target;
    const value = target.value;
    const name = target.name;
    let signedValue = null;

    const controlTypeOptions = modeOptions[controlName];
    // If it is the value input
    if (name === controlTypeOptions.inputName) {
      const parsedValue = parseFloat(value);
      controlTypeOptions.setInputValue(parsedValue);
      signedValue = parsedValue * (controlTypeOptions.radioValue ? -1 : 1);
      // else, if it is the radio button
    } else if (name === controlTypeOptions.radioName) {
      const parsedRadioValue = parseInt(value);
      controlTypeOptions.setRadioValue(parsedRadioValue);
      signedValue = controlTypeOptions.inputValue * (parsedRadioValue ? -1 : 1);
    }
    handleFormChange({
      index,
      key: controlTypeOptions.keyName,
      value: signedValue
    });
  };

  const handlePowerFactorChange = event => {
    const target = event.target;
    const powerFactor = target.value;
    if (controlObj.controlType === 'opModFixedPFAbsorbW') {
      setOpModFixedPFAbsorbWInputValue(powerFactor);
    } else if (controlObj.controlType === 'opModFixedPFInjectW') {
      setOpModFixedPFInjectWInputValue(powerFactor);
    }
    if (powerFactor.indexOf('.') > -1) {
      // If there is a decimal
      handleFormChange({
        index,
        key: 'multiplier',
        // multiplier is the number of decimal places but negative
        value: -Math.abs(powerFactor.split('.')[1].length)
      });
      handleFormChange({
        index,
        key: 'displacement',
        // displacement will be the decimal value multiplied by the absolute value of the multiplier
        // Round to express the multiplied floating point as an integer properly
        value: Math.round(
          powerFactor * Math.pow(10, powerFactor.split('.')[1].length)
        )
      });
    } else if (parseInt(powerFactor) === 1) {
      // Handling no decimal places for max value of 1
      handleFormChange({
        index,
        key: 'multiplier',
        value: -1
      });
      handleFormChange({
        index,
        key: 'displacement',
        value: 10
      });
    } else if (parseInt(powerFactor) === 0) {
      // Handling no decimal places for max value of 1
      handleFormChange({
        index,
        key: 'multiplier',
        value: 0
      });
      handleFormChange({
        index,
        key: 'displacement',
        value: 0
      });
    }
  };

  const handleFindExistingCurveSelect = () => {
    setWaitingForReturn(index);
    dispatch(
      setSidePanel({
        entity: RESOURCE_TYPES.CURVE,
        state: SIDEPANEL_STATES.ADD_EXISTING,
        keepBreadcrumbs: true,
        data: { curveType: controlType }
      })
    );
  };

  const handleAddNewClickCurveSelect = () => {
    setWaitingForReturn(index);
    dispatch(
      setSidePanel({
        entity: RESOURCE_TYPES.CURVE,
        state: SIDEPANEL_STATES.CREATE,
        keepBreadcrumbs: true,
        data: { curveType: controlType }
      })
    );
  };

  const getRowHeight = ({ data }) => {
    const { curveDataArray } = data;

    if (curveDataArray) {
      if (curveDataArray.length > defaultItemsToDisplayInSingleRow) {
        return (
          defaultRowHeight +
          defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow +
          30
        );
      }
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * (curveDataArray.length - 1) +
        30
      );
    }
    return defaultRowHeight;
  };

  const renderForm = () => {
    switch (controlType) {
      case 'opModConnect':
        return (
          <ControlTypeBooleans>
            <RadioLabel>
              <RadioButton
                required
                name={controlType}
                type="radio"
                value={1}
                checked={parseInt(controlObj[controlType]) === 1}
                onChange={handleInputChange}
              />
              <Text fontSize="12px" display="inline-block">
                Connected
              </Text>
            </RadioLabel>
            <RadioLabel>
              <RadioButton
                required
                name={controlType}
                type="radio"
                value={0}
                checked={parseInt(controlObj[controlType]) === 0}
                onChange={handleInputChange}
              />
              <Text fontSize="12px" display="inline-block">
                Disconnected
              </Text>
            </RadioLabel>
          </ControlTypeBooleans>
        );

      case 'opModEnergize':
        return (
          <ControlTypeBooleans>
            <RadioLabel>
              <RadioButton
                required
                name={controlType}
                type="radio"
                value={1}
                checked={parseInt(controlObj[controlType]) === 1}
                onChange={handleInputChange}
              />
              <Text fontSize="12px" display="inline-block">
                Energized
              </Text>
            </RadioLabel>
            <RadioLabel>
              <RadioButton
                required
                name={controlType}
                type="radio"
                value={0}
                checked={parseInt(controlObj[controlType]) === 0}
                onChange={handleInputChange}
              />
              <Text fontSize="12px" display="inline-block">
                De-Energized
              </Text>
            </RadioLabel>
          </ControlTypeBooleans>
        );

      case 'opModFixedPFInjectW':
        return (
          <Fragment>
            <PanelRow>
              <RowDescriptor>Power Factor</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="powerFactor"
                  onChange={handlePowerFactorChange}
                  type="number"
                  min="0"
                  max="1"
                  step="0.0001"
                  key="opModFixedPFInjectWLabledInput"
                  value={opModFixedPFInjectWInputValue}
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModFixedPFInjectW.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            {/* boolean */}
            <PanelRow>
              <RowDescriptor>Excitation</RowDescriptor>
              <RadioBooleans>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name="pfinjectExcitation"
                    type="radio"
                    value={1}
                    checked={parseInt(controlObj.pfinjectExcitation) === 1}
                    onChange={handleInputChange}
                  />
                  Under-excited
                </RadioLabel>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name="pfinjectExcitation"
                    type="radio"
                    value={0}
                    checked={parseInt(controlObj.pfinjectExcitation) === 0}
                    onChange={handleInputChange}
                  />
                  Over-excited
                </RadioLabel>
              </RadioBooleans>
            </PanelRow>
          </Fragment>
        );
      case 'opModFixedPFAbsorbW':
        return (
          <Fragment>
            {/* int 0 to 65535 */}
            <PanelRow>
              <RowDescriptor>Power Factor</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="powerFactor"
                  onChange={handlePowerFactorChange}
                  type="number"
                  min="0"
                  max="1"
                  step="0.0001"
                  key="opModFixedPFAbsorbWLabledInput"
                  value={opModFixedPFAbsorbWInputValue}
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModFixedPFAbsorbW.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            {/* boolean */}
            <PanelRow>
              <RowDescriptor>Excitation</RowDescriptor>
              <RadioBooleans>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name="pfabsorbExcitation"
                    type="radio"
                    value={1}
                    checked={parseInt(controlObj.pfabsorbExcitation) === 1}
                    onChange={handleInputChange}
                  />
                  Under-excited
                </RadioLabel>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name="pfabsorbExcitation"
                    type="radio"
                    value={0}
                    checked={parseInt(controlObj.pfabsorbExcitation) === 0}
                    onChange={handleInputChange}
                  />
                  Over-excited
                </RadioLabel>
              </RadioBooleans>
            </PanelRow>
          </Fragment>
        );

      case 'opModFixedVar':
        return (
          <Fragment>
            <PanelRow>
              <RowDescriptor>Ref Type</RowDescriptor>
              <Dropdown
                name="refType"
                onChange={handleInputChange}
                value={controlObj.refType}
                required
              >
                <option value="">Select a Ref Type</option>
                {OP_MOD_FIXED_VAR_REF_TYPES.map(obj => {
                  return (
                    <option key={obj.value} value={obj.value}>
                      {obj.displayText}
                    </option>
                  );
                })}
              </Dropdown>
            </PanelRow>

            {/* int -10000 to 10000 */}
            <PanelRow>
              <RowDescriptor>Value</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="value"
                  onChange={handleInputChange}
                  type="number"
                  min="-100"
                  max="100"
                  step="0.01"
                  key="opModFixedVarLabledInput"
                  value={controlObj.value}
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModFixedVar.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>
          </Fragment>
        );

      case 'opModFixedW':
        return (
          <Fragment>
            {/* boolean */}
            <PanelRow>
              <RowDescriptor>Mode</RowDescriptor>
              <RadioBooleans>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name={controlModeNames.opModFixedW.radio}
                    type="radio"
                    value={1}
                    checked={opModFixedWRadioValue === 1}
                    onChange={e =>
                      handleChargeDischargeChange(e, 'opModFixedW')
                    }
                  />
                  Charge
                </RadioLabel>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name={controlModeNames.opModFixedW.radio}
                    type="radio"
                    value={0}
                    checked={opModFixedWRadioValue === 0}
                    onChange={e =>
                      handleChargeDischargeChange(e, 'opModFixedW')
                    }
                  />
                  Discharge
                </RadioLabel>
              </RadioBooleans>
            </PanelRow>

            <PanelRow>
              <RowDescriptor>Rate</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name={controlModeNames.opModFixedW.value}
                  onChange={e => handleChargeDischargeChange(e, 'opModFixedW')}
                  type="number"
                  min="0"
                  max="100"
                  step="0.001"
                  value={opModFixedWInputValue}
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModFixedW.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>
          </Fragment>
        );

      case 'opModFreqDroop':
        return (
          <>
            <PanelRow>
              <RowDescriptor>Over-frequency Dead Band</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="dBOF"
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                  max="4294967.295"
                  step="0.001"
                  key="opModFreqDroopOfDbLabledInput"
                  value={controlObj.dBOF}
                  required
                />
                <InputUnitLabel>Hz</InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            <PanelRow>
              <RowDescriptor>Under-Frequency Dead Band</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="dBUF"
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                  max="4294967.295"
                  step="0.001"
                  key="opModFreqDroopUfDbLabledInput"
                  value={controlObj.dBUF}
                  required
                />
                <InputUnitLabel>Hz</InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            <PanelRow>
              <RowDescriptor>Over-frequency Frequency Change</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="kOF"
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                  max="65.535"
                  step="0.001"
                  key="opModFreqDroopOfFcLabledInput"
                  value={controlObj.kOF}
                  required
                />
              </InputAndLabelContainer>
            </PanelRow>

            <PanelRow>
              <RowDescriptor>Under-Frequency Frequency Change</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="kUF"
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                  max="65.535"
                  step="0.001"
                  key="opModFreqDroopUfFcLabledInput"
                  value={controlObj.kUF}
                  required
                />
              </InputAndLabelContainer>
            </PanelRow>

            <PanelRow>
              <RowDescriptor>Open Loop Response Time</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="openLoopTms"
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                  max="655.35"
                  step="0.01"
                  key="opModFreqDroopOpenLoopLabledInput"
                  value={controlObj.openLoopTms}
                  required
                />
                <InputUnitLabel>{`s`}</InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>
          </>
        );

      case 'opModMaxLimW':
        return (
          <PanelRow>
            {/* int between 0 and 10000 */}
            {/* 1/100th of % */}
            <RowDescriptor>Max Active</RowDescriptor>
            <InputAndLabelContainer>
              <LabledInput
                name={controlType}
                onChange={handleInputChange}
                type="number"
                min="0"
                max="100"
                step="0.01"
                key="opModMaxLimWLabledInput"
                required
              />
              <InputUnitLabel>{CONTROL_TYPES.opModMaxLimW.unit}</InputUnitLabel>
            </InputAndLabelContainer>
          </PanelRow>
        );

      case 'rampTms':
        return (
          <PanelRow>
            <RowDescriptor>Ramp Rate</RowDescriptor>
            <InputAndLabelContainer>
              <LabledInput
                name={controlType}
                onChange={handleInputChange}
                type="number"
                min="0"
                max="655.35"
                step="0.01"
                key="rampTmsLabledInput"
                required
              />
              <InputUnitLabel>{CONTROL_TYPES.rampTms.unit}</InputUnitLabel>
            </InputAndLabelContainer>
          </PanelRow>
        );

      case 'opModTargetVar':
        return (
          <div>
            {/* int -32768 to 32767 */}
            <PanelRow>
              <RowDescriptor>Value (Var)</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="value"
                  onChange={handleInputChange}
                  value={controlObj.value}
                  type="number"
                  min="-32768"
                  max="32767"
                  key="opModTargetVarValueLabledInput"
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModTargetVar.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            {/* int -9 to 9 */}
            <PanelRow>
              <RowDescriptor>Multiplier</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="multiplier"
                  onChange={handleInputChange}
                  value={controlObj.multiplier}
                  type="number"
                  min="-9"
                  max="9"
                  key="opModTargetVarMultiplierLabledInput"
                  required
                />
                <InputUnitLabel>x10ˣ</InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>
          </div>
        );

      case 'opModTargetW':
        return (
          <div>
            {/* int -32768 to 32767 */}
            <PanelRow>
              <RowDescriptor>Mode</RowDescriptor>
              <RadioBooleans>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name={controlModeNames.opModTargetW.radio}
                    type="radio"
                    value={1}
                    checked={opModTargetWRadioValue === 1}
                    onChange={e =>
                      handleChargeDischargeChange(e, 'opModTargetW')
                    }
                  />
                  Charge
                </RadioLabel>
                <RadioLabel fontSize="12px">
                  <RadioButton
                    required
                    name={controlModeNames.opModTargetW.radio}
                    type="radio"
                    value={0}
                    checked={opModTargetWRadioValue === 0}
                    onChange={e =>
                      handleChargeDischargeChange(e, 'opModTargetW')
                    }
                  />
                  Discharge
                </RadioLabel>
              </RadioBooleans>
            </PanelRow>
            <PanelRow>
              <RowDescriptor>Value (Watts)</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name={controlModeNames.opModTargetW.value}
                  onChange={e => handleChargeDischargeChange(e, 'opModTargetW')}
                  value={opModTargetWInputValue}
                  type="number"
                  min="0"
                  max="32767"
                  key="opModTargetWValueLabledInput"
                  required
                />
                <InputUnitLabel>
                  {CONTROL_TYPES.opModTargetW.unit}
                </InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>

            {/* int -9 to 9 */}
            <PanelRow>
              <RowDescriptor>Multiplier</RowDescriptor>
              <InputAndLabelContainer>
                <LabledInput
                  name="multiplier"
                  onChange={handleInputChange}
                  value={controlObj.multiplier}
                  type="number"
                  min="-9"
                  max="9"
                  key="opModTargetWMultiplierLabledInput"
                  required
                />
                <InputUnitLabel>x10ˣ</InputUnitLabel>
              </InputAndLabelContainer>
            </PanelRow>
          </div>
        );

      case 'opModFreqWattCurveId':
      case 'opModHFRTMayTripCurveId':
      case 'opModHFRTMustTripCurveId':
      case 'opModHVRTMayTripCurveId':
      case 'opModHVRTMomentaryCessationCurveId':
      case 'opModHVRTMustTripCurveId':
      case 'opModLFRTMayTripCurveId':
      case 'opModLFRTMustTripCurveId':
      case 'opModLVRTMayTripCurveId':
      case 'opModLVRTMomentaryCessationCurveId':
      case 'opModLVRTMustTripCurveId':
      case 'opModVoltVarCurveId':
      case 'opModVoltWattCurveId':
      case 'opModWattPFCurveId':
      case 'opModWattVarCurveId':
        return (
          <Fragment>
            {controlObj.curve ? (
              <CurveDisplayRow>
                <RemoveCurveButton
                  type="button"
                  onClick={() => removeCurve(index)}
                >
                  <RemoveIcon fill="#0779af" width="14" />
                </RemoveCurveButton>
                <Table
                  columnDefs={curveColumnDefs}
                  rowData={[controlObj.curve]}
                  height="200px"
                  width="100%"
                  padding="0px"
                  pagination={false}
                  getRowHeight={getRowHeight}
                />
              </CurveDisplayRow>
            ) : (
              <div>
                <BlackBorderButton
                  type="button"
                  margin="0 8px 0 0"
                  onClick={() => handleFindExistingCurveSelect()}
                  isError={isError}
                >
                  Find Existing Curve
                </BlackBorderButton>
                <BlackBorderButton
                  type="button"
                  onClick={() => handleAddNewClickCurveSelect()}
                  isError={isError}
                >
                  + New Curve
                </BlackBorderButton>
              </div>
            )}
          </Fragment>
        );
      default:
        return null;
    }
  };

  return (
    <ControlTypeFormWrapper>
      {controlType ? renderForm() : null}
    </ControlTypeFormWrapper>
  );
};

export default ControlTypeForm;
